import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

// Öffentliche Selbst-Anmeldung für Trainingscamps (Teilnehmer tragen ihre
// Passdaten selbst ein → landen direkt im Camp; kein Login nötig).
// Zweisprachig Englisch/Deutsch (Team Ghana + DE).

const C = { bg: '#0b0c10', card: '#15162c', red: '#c0392b', gold: '#f0c14b', border: 'rgba(255,255,255,0.12)', text: '#f2f2f6', muted: 'rgba(255,255,255,0.55)' };

const fmt = (d) => { try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return d; } };

const S = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'system-ui, Arial, sans-serif', padding: '2rem 1rem' },
  wrap: { maxWidth: 720, margin: '0 auto' },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.75rem', marginTop: '1.25rem' },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0 },
  meta: { color: C.muted, marginTop: '0.4rem', fontSize: '0.95rem', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginTop: '0.5rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  full: { gridColumn: '1 / -1' },
  label: { fontSize: '0.8rem', color: C.muted },
  input: { padding: '0.6rem 0.7rem', borderRadius: 9, border: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.3)', color: C.text, fontSize: '0.95rem', width: '100%', boxSizing: 'border-box' },
  section: { marginTop: '1.25rem', fontWeight: 700, color: C.gold, fontSize: '0.9rem', borderTop: `1px solid ${C.border}`, paddingTop: '1rem' },
  btn: { marginTop: '1.4rem', width: '100%', padding: '0.85rem', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg, #f7d98f, #d9aa43)', color: '#0b0c10', fontWeight: 800, fontSize: '1rem', cursor: 'pointer' },
  err: { marginTop: '1rem', background: 'rgba(192,57,43,0.15)', border: `1px solid ${C.red}`, borderRadius: 9, padding: '0.7rem 0.9rem', color: '#ffb4a8' },
};

export default function CampRegistrationPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [regResult, setRegResult] = useState(null);   // { id, token } nach Anmeldung
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/events/public')
      .then(r => r.json())
      .then(d => { const e = (d.data || []).find(x => String(x.event_id) === String(id)); setEvent(e || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const fld = (k, label, type = 'text', full = false) => (
    <div style={{ ...S.field, ...(full ? S.full : {}) }} key={k}>
      <label style={S.label}>{label}</label>
      <input style={S.input} type={type} value={form[k] || ''} onChange={set(k)} />
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.vorname || !form.nachname) { setError('Please enter first and last name / Bitte Vor- und Nachname angeben.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/events/${id}/anmeldungen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { setRegResult(data.data || null); setDone(true); window.scrollTo(0, 0); }
      else setError(data.error || 'Registration failed / Anmeldung fehlgeschlagen.');
    } catch (_) { setError('Network error / Netzwerkfehler.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={S.page}><div style={S.wrap}><p style={{ color: C.muted }}>Loading…</p></div></div>;

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <h1 style={S.h1}>🥋 {event ? event.name : 'Training Camp'}</h1>
        <div style={S.meta}>
          {event && <>📅 {fmt(event.datum)}{event.end_datum ? ` – ${fmt(event.end_datum)}` : ''} · 📍 {event.ort}<br /></>}
          <span>Registration for participants · Anmeldung für Teilnehmer</span>
        </div>

        {done ? (
          <div style={S.card}>
            <h2 style={{ marginTop: 0, color: C.gold }}>✅ Thank you! / Vielen Dank!</h2>
            <p>Your registration has been received. You can download your official invitation letter for the visa application right now.</p>
            <p style={{ color: C.muted }}>Deine Anmeldung ist eingegangen. Du kannst dein offizielles Einladungsschreiben für den Visumantrag jetzt direkt herunterladen.</p>
            {regResult?.token && (
              <a style={{ ...S.btn, display: 'block', textAlign: 'center', textDecoration: 'none' }}
                 href={`/api/events/${id}/visa/brief-public/${regResult.id}.pdf?token=${regResult.token}`}
                 target="_blank" rel="noreferrer">
                ⬇︎ Download invitation letter (PDF) · Einladungsbrief herunterladen
              </a>
            )}
            <button style={{ ...S.btn, marginTop: '0.8rem', background: 'rgba(255,255,255,0.08)', color: C.text }}
                    onClick={() => { setForm({}); setRegResult(null); setDone(false); }}>
              Register another person / Weitere Person anmelden
            </button>
          </div>
        ) : (
          <form style={S.card} onSubmit={submit}>
            <p style={{ marginTop: 0, color: C.muted, fontSize: '0.9rem' }}>Please fill in exactly as written in the passport. / Bitte genau wie im Reisepass angeben.</p>

            <div style={S.grid}>
              {fld('vorname', 'First name / Vorname *')}
              {fld('nachname', 'Surname / Nachname *')}
              <div style={S.field}>
                <label style={S.label}>Gender / Geschlecht</label>
                <select style={S.input} value={form.geschlecht || ''} onChange={set('geschlecht')}>
                  <option value="">—</option>
                  <option value="männlich">male / männlich</option>
                  <option value="weiblich">female / weiblich</option>
                  <option value="divers">diverse / divers</option>
                </select>
              </div>
              {fld('geburtsdatum', 'Date of birth / Geburtsdatum', 'date')}
              {fld('geburtsort', 'Place of birth / Geburtsort')}
              {fld('geburtsland', 'Country of birth / Geburtsland')}
              {fld('nationalitaet', 'Nationality / Staatsangehörigkeit')}
            </div>

            <div style={S.section}>Passport / Reisepass</div>
            <div style={S.grid}>
              {fld('pass_nummer', 'Passport No. / Reisepass-Nr.')}
              {fld('pass_ausstellungsort', 'Place of issue / Ausstellungsort')}
              {fld('pass_ausstellungsdatum', 'Date of issue / Ausgestellt am', 'date')}
              {fld('pass_ablaufdatum', 'Valid until / Gültig bis', 'date')}
            </div>

            <div style={S.section}>Home address / Wohnanschrift</div>
            <div style={S.grid}>
              {fld('adresse_strasse', 'Street & no. / Straße & Nr.', 'text', true)}
              {fld('adresse_plz', 'Postal code / PLZ')}
              {fld('adresse_ort', 'City / Ort')}
              {fld('adresse_land', 'Country / Land')}
            </div>

            <div style={S.section}>Contact / Kontakt</div>
            <div style={S.grid}>
              {fld('email', 'Email (optional)', 'email')}
              {fld('telefon', 'Phone / Telefon (optional)')}
            </div>

            {error && <div style={S.err}>{error}</div>}
            <button style={S.btn} type="submit" disabled={submitting}>{submitting ? 'Sending… / Senden…' : 'Submit registration / Anmeldung absenden'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
